// Authentication plugin.
import { parseSpidrServices } from './services'

// Other plugins.
import request from '../../request/effects'
import BasicError, { authCodes } from '../../errors'
import { logManager } from '../../logs'

// Libraries.
import { call } from 'redux-saga/effects'
import { mergeValues } from '../../common/utils'

const log = logManager.getLogger('AUTH')

/**
 * Subscribe to SPiDR with the provided information.
 * @method subscribe
 * @param  {Object} connection Server information.
 * @param  {Object} credentials User information.
 * @return {Object} Subscription response.
 */
export function * subscribe (connection, credentials, extras = {}) {
  /*
   * TODO: Clean-up / fix the function signature. The requestOptions (eg. headers)
   *    are mixed with the API options (eg. client correlator) when they're used
   *    for unrelated purposes.
   */
  const subscribeType = connection.isAnonymous ? 'anonymous' : 'user'
  let requestOptions = {}
  requestOptions.method = 'POST'

  requestOptions.url =
    `${connection.protocol}://${connection.server}:${connection.port}` +
    `/rest/version/${connection.version}` +
    `/${subscribeType}/${credentials.username}/subscription`

  requestOptions.body = JSON.stringify({
    subscribeRequest: {
      expires: connection.expires,
      service: connection.service,
      localization: connection.localization || 'English_US',
      useTurn: connection.useTurn || true,
      notificationType: connection.notificationType || 'WebSocket',
      supported: ['RingingFeedback'],
      forceLogOut: (extras.forceLogOut || false).toString(),
      clientCorrelator: extras.clientCorrelator
    }
  })

  // If there were any extra request options, merge them into the
  //      options for this request. Priority is for the options defined here.
  requestOptions = mergeValues(extras, requestOptions)

  log.info(`Subscribing user ${credentials.username}`)
  // Send the subscription request.
  const response = yield request(requestOptions)

  if (response.error) {
    if (response.payload.body) {
      const body = response.payload.body

      let statusCode
      if (body.statusCode && body.reason) {
        /*
         * In some cases, the response is not wrapped in a `subscribeResponse`
         *    property. This seems to be when using a pre-provisioned user (stored
         *    as part of KL?) rather than a dynamically created user (retrieved
         *    from AS?).
         * Reference: ABE-23981 (and KAA-1937)
         */
        statusCode = body.statusCode
      } else if (body.authorizationResponse && body.authorizationResponse.statusCode) {
        /*
         * In other cases, the response is wrapped in a `authorizationResponse`
         *    property instead. This seems to be when an anonymous user
         *    subscription fails (a success has `subscribeResponse`).
         * Reference: KAA-2440
         */
        statusCode = body.authorizationResponse.statusCode
      } else {
        statusCode = body.subscribeResponse.statusCode
      }
      log.debug(`Failed user subscription with status code ${statusCode}.`)

      // Handle errors from the server.
      return {
        // TODO: Better error; more info.
        error: new BasicError({
          message: `Failed to subscribe user. Code: ${statusCode}.`,
          code: authCodes.LINK_SUBSCRIBE_FAIL
        })
      }
    } else {
      log.debug('Failed user subscription.', response.payload.result.message)
      // Handle errors from the request plugin.
      return {
        // TODO: Better error; more info.
        error: new BasicError({
          message: `Subscribe request failed: ${response.payload.result.message}.`,
          // TODO: Shared error codes.
          code: authCodes.LINK_SUBSCRIBE_FAIL
        })
      }
    }
  } else {
    // Request was successful.
    const subscribeResponse = response.payload.body.subscribeResponse

    const subscribedServices = subscribeResponse.subscriptionParams.service
    const servicesInfo = yield call(parseSpidrServices, connection.service, subscribedServices)
    log.debug(`Successfully subscribed user. Service subscription status: ${servicesInfo.status}`)

    return {
      error: false,
      servicesInfo,
      ...subscribeResponse
    }
  }
}

/**
 * Unsubscribe from SPiDR with the provided subscription info.
 * @method unsubscribe
 * @param  {Object}    connection Server information for the service in use.
 * @param  {string}    connection.server Server information for generating the URL.
 * @param  {string}    connection.requestOptions Common request options to be added.
 * @param  {string}    subscriptionURL URL of the user's subscription instance.
 */
export function * unsubscribe (connection, subscriptionURL) {
  const requestOptions = {}
  requestOptions.method = 'DELETE'

  requestOptions.url =
    `${connection.server.protocol}://${connection.server.server}:${connection.server.port}` + subscriptionURL

  log.info('Unsubscribing user.')
  // Send the unsubscribe request.
  const response = yield request(requestOptions)

  /*
   * No matter what the response was, we still want to handle the user as
   *    unsubscribed afterwards. If the request fails, there isn't a point to
   *    keep the user subscribed since they want to unsubscribe anyway.
   * For debug purposes, log what the response was but handle it as a success.
   */
  if (response.error) {
    if (response.payload.body) {
      // Handle errors from the server.
      const { statusCode } = response.payload.body.subscribeResponse
      log.debug(`Encountered error unsubscribing user with status code ${statusCode}.`)
    } else {
      // Handle errors from the request helper.
      const { message } = response.payload.result
      log.debug('Encountered error unsubscribing user.', message)
    }
  } else {
    // Request was successful.
    log.debug('User unsubscribed successfully.')
  }
}

/**
 * Resubscribe to SPiDR to extend an existing subscription.
 * @method resubscribe
 * @param  {Object}    connection Server information for the service in use.
 * @param  {string}    connection.server Server information for generating the URL.
 * @param  {string}    connection.requestOptions Common request options to be added.
 * @param  {Object}    subscription Information about the subscription instance.
 * @param  {string}    subscription.expires - The time (in seconds) until subscription expiry.
 * @param  {Array}     subscription.service - The SPiDR services to resubscribe to.
 * @param  {Array}     subscription.url - The URL of the user's subscription instance.
 * @return {Object}    Resubscription response.
 */
export function * resubscribe (connection, subscription) {
  const requestOptions = {}
  requestOptions.method = 'PUT'

  if (subscription.clientCorrelator) {
    requestOptions.url =
      `${connection.server.protocol}://${connection.server.server}:${connection.server.port}` +
      `/rest/version/${connection.server.version}` +
      `/user/${connection.username}/subscription/clientCorrelator/${subscription.clientCorrelator}`
  } else {
    requestOptions.url =
      `${connection.server.protocol}://${connection.server.server}:${connection.server.port}` + subscription.url
  }

  // TODO: Don't hardcode the defaults here. Should be shared with
  //      the subscribe request as well.
  requestOptions.body = JSON.stringify({
    subscribeRequest: {
      expires: subscription.expires,
      service: subscription.service,
      localization: subscription.localization || 'English_US',
      useTurn: subscription.useTurn || true,
      notificationType: subscription.notificationType || 'WebSocket'
    }
  })

  log.info('Resubscribing user.')
  // Send the subscription update request.
  const response = yield request(requestOptions)

  if (response.error) {
    if (response.payload.body) {
      // Handle errors from the server.
      const { statusCode } = response.payload.body.subscribeResponse
      log.debug(`Failed to update user subscription with status code ${statusCode}.`)

      let message, errorCode
      if (response.payload.result.code === 404 && [4, 39].includes(statusCode)) {
        /*
         * Special case: Status code 39 (for KL) or 4 (for UC) means the subscription
         *    does not exist (ie. the request 404'd). We want to treat this error
         *    differently because it is a "subscription gone" scenario and we need
         *    to clear the SDK's subscription info.
         */
        message = `Subscription no longer exists. Code ${statusCode}.`
        // TODO: We shouldn't be assigning arbitrary codes to BasicError (ie. only
        //    use the codes pre-defined) because the application also gets access
        //    to this, but we don't have a good way to pass specific error info
        //    around for side-effects (ie. subscription gone).
        errorCode = statusCode
      } else {
        message = `Failed to update user subscription with status code ${statusCode}.`
        errorCode = authCodes.LINK_UPDATE_SUBSCRIPTION_FAIL
      }

      return {
        // TODO: Better error; more info.
        error: new BasicError({ message, code: errorCode })
      }
    } else {
      // Handle errors from the request helper.
      const { message } = response.payload.result
      log.debug('User subscription update request failed.', message)

      return {
        // TODO: Better error; more info.
        error: new BasicError({
          message: `Update subscription request failed.: ${message}.`,
          // TODO: Shared error codes.
          code: authCodes.LINK_UPDATE_SUBSCRIPTION_FAIL
        })
      }
    }
  } else {
    // Request was successful.
    const resubResponse = response.payload.body.subscribeResponse

    if (resubResponse.statusCode === 0 || resubResponse.statusCode === 2) {
      const subscribedServices = resubResponse.subscriptionParams.service
      const servicesInfo = yield call(parseSpidrServices, subscription.service, subscribedServices)
      log.debug(`Successfully resubscribed user. Service resubscription status: ${servicesInfo.status}`)

      // Success.
      return {
        error: false,
        servicesInfo,
        ...resubResponse
      }
    } else {
      log.info('Failed to resubscribe user. Status Code: ', resubResponse.statusCode)
      // Unknown statusCode, consider as failure.
      return {
        error: new BasicError({
          message: `Unknown error; statusCode: ${resubResponse.statusCode}.`,
          code: authCodes.LINK_UPDATE_SUBSCRIPTION_FAIL
        })
      }
    }
  }
}
